import React from "react";
import { Helmet } from "react-helmet-async";
import Edit, { Columns } from "../../component/Edit";
import { deleteRole, getRole, getOperation, saveRole, type RoleData, type OperationData } from "../../api/admin";
import Toast from "../../component/toast";
import { CancelablePromise } from "../../util/client";
import { useCrumbs } from "../../component/breadcrumbs";
import useLocale from "../../util/i18n";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function RoleEdit() {
    const {0: __} = useLocale();
    const {0: setCrumbs} = useCrumbs();
    let navigate = useNavigate();
    let columns = React.useMemo<Columns>(() => ({
        [__('General')]: [
            {
                name: 'id',
                type: 'hidden'
            },
            {
                name: 'name',
                required: true,
                label: __('Name'),
                type: 'locale'
            },
            {
                name: 'status',
                label: __('Status'),
                type: 'select',
                required: true,
                options: [
                    { value: '1', label: __('Enabled') },
                    { value: '0', label: __('Disabled') }
                ]
            }
        ],
        [__('Privilege')]: [
            {
                name: 'operation[]',
                type: 'component',
                component: Operations
            }
        ]
    }), [__]);
    const doLoad = React.useCallback((params: Record<string, any>) => {
        let p = getRole({ filter: { id: params.id }, limit: 1 });
        return new CancelablePromise<RoleData>((resolve, reject) => p.then(response => {
            if (typeof response !== 'string' && response.data.length) {
                if (typeof response.data[0].name === 'string') {
                    response.data[0].name = JSON.parse(response.data[0].name);
                }
                resolve(response.data[0]);
            } else {
                reject(response);
            }
        }), p.doCancel);
    }, []);
    const doSave = React.useCallback((data: FormData) => {
        saveRole(data).then(response => {
            if (typeof response === 'string') {
                Toast.show(response, 'success');
            } else {
                navigate('/role/edit?id=' + response.data);
                Toast.show(response.message, 'success');
            }
        });
    }, [navigate]);
    const doDelete = React.useCallback((data: Record<string, any>) => {
        if (window.confirm(__('Are you sure to delete this?'))) {
            deleteRole({ id: data.id }).then(response => {
                navigate('/role');
                Toast.show(response, 'success');
            });
        }
    }, [navigate]);
    React.useEffect(() => {
        setCrumbs([{ label: 'Role Management', link: '/role' }, 'Edit']);
    }, [setCrumbs]);
    return (
        <>
            <Helmet>
                <title>{__('Edit %s').replace('%s', __('Role'))}</title>
            </Helmet>
            <Edit id="id" columns={columns} doLoad={doLoad} doSave={doSave} doDelete={doDelete} />
        </>
    );
}

function Operations() {
    let { 0: search } = useSearchParams();
    let { 0: operations, 1: setOperations } = React.useState<Array<OperationData>>([]);
    let { 0: __ } = useLocale();
    let onChange = React.useCallback((i: number, e: React.ChangeEvent<HTMLInputElement>) => {
        setOperations(old => {
            let cloned = [...old];
            cloned[i].granted = e.target.checked;
            return cloned;
        });
    }, [setOperations]);
    React.useEffect(() => {
        getOperation({ filter: { granted: search.get('id') ?? undefined }, limit: -1 }).then(response => {
            if (typeof response !== 'string') {
                response.data.sort((a, b) => a.alias > b.alias ? 1 : (a.alias === b.alias ? 0 : -1));
                setOperations(response.data);
            }
        });
        getOperation()
    }, [setOperations, search]);
    return (
        <div className="row">
            {operations.map(((operation, index) => (
                <label key={operation.code} className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2">
                    <input type="checkbox" name="operation[]" className="me-2" checked={operation.granted === true} onChange={onChange.bind(null, index)} value={operation.code} />
                    <span>{__(operation.description)}</span>
                </label>
            )))}
        </div>
    );
}
